/* eslint-disable security/detect-non-literal-regexp */
/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as React from 'react';
import { groupBy, startCase } from 'lodash';
import { IEveStoreSelectorViewProps } from './eve-store-selector';
import classNames from 'classnames';
import {
    IGetFullStoreLocationsWithAvailabilitiesData,
    GetFullStoreLocationsWithAvailabilitiesInput
} from '../../actions/get-full-store-locations-with-availabilities.action';

type ContextValues = {
    states: string[];
    selectedState: string;
    setSelectedState: React.Dispatch<React.SetStateAction<string>>;
    locations: IGetFullStoreLocationsWithAvailabilitiesData['Locations'];
} & IEveStoreSelectorViewProps;

const Context = React.createContext({} as ContextValues);

export default (props: IEveStoreSelectorViewProps) => {
    const { result } = props.data.fullStoreLocationsWithAvailabilities;
    const locations = [...(result?.Locations ?? [])];
    const [filteredLocations] = React.useState<typeof locations>();
    const locationsByState = groupBy(filteredLocations ?? locations, 'State') as Record<
        string,
        IGetFullStoreLocationsWithAvailabilitiesData['Locations']
    >;
    const states = Object.keys(locationsByState).sort();
    const [selectedState, setSelectedState] = React.useState(states[0]);

    React.useEffect(() => {
        const _location = findLocationById(result?.SelectedId, locations);
        const _state = _location?.State;
        if (_state) {
            setSelectedState(_state);
        }
    }, [result?.SelectedId]);

    return (
        <Context.Provider
            value={{ ...props, states, selectedState, setSelectedState, locations: (locationsByState ?? {})[`${selectedState}`] ?? [] }}
        >
            <div className='ms-store-locate' data-m-t='store-selector' aria-label='Store Locator'>
                <div className='ms-store-select__body'>
                    <div className='ms-store-select__store_container'>
                        <Search />
                        <div className='ms-store-select__body_wrapper'>
                            <div className='ms-store-select__store_container'>
                                <div className='ms-store-select__locations show' role='list'>
                                    <StateList />
                                    <LocationList />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Context.Provider>
    );
};

function StateList() {
    const { states, selectedState, setSelectedState } = useContext();
    const jsx = (
        <div className='cps-store-locator__state-wrapper'>
            {states.map(state => (
                <button
                    key={state}
                    className={classNames('cps-store-locator__state', selectedState === state && 'selected')}
                    onClick={() => setSelectedState(state)}
                >
                    {state}
                </button>
            ))}
        </div>
    );
    return <>{jsx}</>;
}

function LocationList() {
    const { context, locations, data } = useContext();
    const focusedLocationId = data.fullStoreLocationsWithAvailabilities.result?.SelectedId;

    const jsx = locations.map((item, i) => {
        const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
        const weekdayHourData = Object.entries(item.Hours).filter(([key]) => !/OpenFrom|OpenTo/.test(key));
        const hasWeekdayHourData = weekdayHourData.some(([, value]) => !!value);
        return (
            <div
                key={i}
                className='ms-store-select__location-wrapper'
                onClick={() => {
                    context.actionContext.update<IGetFullStoreLocationsWithAvailabilitiesData>(
                        new GetFullStoreLocationsWithAvailabilitiesInput(),
                        // @ts-expect-error
                        { ...data.fullStoreLocationsWithAvailabilities.result, SelectedId: item?.Id }
                    );
                    if ([item.Latitude, item.Longitude].every(value => value !== undefined)) {
                        window.map.setCamera({ center: [item.Longitude, item.Latitude], zoom: 10 });
                    }
                }}
            >
                <div className='ms-store-select__location' role='listitem'>
                    <div className='ms-store-select__location-line-item'>
                        <div className='ms-store-select__location-line-item-header'>
                            <svg
                                className='ms-store-select__location-line-item-store-index'
                                fill='#d95426'
                                viewBox='0 0 384 512'
                                style={{ width: '26px', height: '35px' }}
                            >
                                <path d='M172.3 501.7C27 291 0 269.4 0 192 0 86 86 0 192 0s192 86 192 192c0 77.4-27 99-172.3 309.7-9.5 13.8-29.9 13.8-39.5 0z'></path>
                                <text textAnchor='middle' style={{ fill: 'white' }} x='185' width='20' fontSize='12rem' y='270'>
                                    {i + 1}
                                </text>
                            </svg>
                            <span className='ms-store-select__location-line-item-store-name'>
                                {getFullStateName(item.State.toUpperCase())} - {item.StorePurpose}
                            </span>
                        </div>
                        <div className='ms-store-select__location-line-item-content cps-location-item__container'>
                            <div>
                                <div className='ms-store-select__location-line-item-contact-info'>
                                    <p className='ms-store-select__location-line-header'>{item.StoreName}</p>
                                    <div
                                        className='ms-store-select__location-line-shop-address-section'
                                        aria-label='436 N Austin Dr Chandler, AZ 85226-2634 USA'
                                        role='button'
                                    >
                                        <span
                                            className='msi msi-map-marker-alt ms-store-select__location-line-shop-address-glyph-icon'
                                            aria-hidden='true'
                                        ></span>
                                        <div className='ms-store-select__location-line-shop-address-text' aria-hidden='true'>
                                            {item.StreetAddress}
                                        </div>
                                        <div className='ms-store-select__location-line-shop-address-text'>
                                            {item.City}, {item.State} {item.Zip}
                                        </div>
                                    </div>

                                    <div style={{ marginTop: '1rem' }}>
                                        {item.Phones.map(({ Type, Value }, j) =>
                                            Type.length && Value.length ? (
                                                <div className='ms-store-select__location-line-item-contact-info' key={j}>
                                                    <span
                                                        className='ms-store-select__location-line-header'
                                                        style={{ textTransform: 'none' }}
                                                    >
                                                        {startCase(Type)}:{' '}
                                                    </span>
                                                    <span className=''>{Value}</span>
                                                </div>
                                            ) : null
                                        )}
                                    </div>
                                </div>
                                <div className='ms-store-select__location-line-item-store-hours'>
                                    <p className='ms-store-select__location-line-header'>Store hours</p>
                                    {item.Hours.OpenFrom && item.Hours.OpenTo && !hasWeekdayHourData ? (
                                        <div className='ms-store-select__store-hours-details' style={{ width: '28ch' }}>
                                            M-F: {secondsToTime(item.Hours.OpenFrom)} - {secondsToTime(item.Hours.OpenTo)}
                                        </div>
                                    ) : null}
                                    {hasWeekdayHourData ? (
                                        <div className='ms-store-select__store-hours-details' style={{ flexDirection: 'column' }}>
                                            {days.map((day, i) => {
                                                const generalOpenTime = secondsToTime(item.Hours.OpenFrom);
                                                const generalCloseTime = secondsToTime(item.Hours.OpenTo);
                                                const isClosed =
                                                    weekdayHourData.filter(([key]) =>
                                                        new RegExp(`IsClose.*${day}`, 'gi').test(key)
                                                    )[0]?.[1] === true;
                                                const openTime = item.Hours[`${day}OpenTime`];
                                                const closeTime = item.Hours[`${day}CloseTime`];

                                                if (/saturday|sunday/gi.test(day) && (openTime || closeTime)) {
                                                    return (
                                                        <div key={i} style={{ whiteSpace: 'nowrap', width: '28ch' }}>
                                                            {day}: {openTime ? secondsToTime(openTime) : generalOpenTime} -{' '}
                                                            {closeTime ? secondsToTime(closeTime) : generalCloseTime}
                                                        </div>
                                                    );
                                                }

                                                if (/saturday|sunday/gi.test(day)) {
                                                    return;
                                                }

                                                if (isClosed) {
                                                    return (
                                                        <div key={i} style={{ whiteSpace: 'nowrap', width: '28ch' }}>
                                                            {day}: Closed
                                                        </div>
                                                    );
                                                }

                                                return (
                                                    <div key={i} style={{ whiteSpace: 'nowrap', width: '28ch' }}>
                                                        {day}: {openTime ? secondsToTime(openTime) : generalOpenTime} -{' '}
                                                        {closeTime ? secondsToTime(closeTime) : generalCloseTime}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    ) : null}
                                </div>
                                <div></div>
                                {item.SquareFootage ? (
                                    <div className='ms-store-select__location-line-item-contact-info'>
                                        <span className='ms-store-select__location-line-header' style={{ textTransform: 'none' }}>
                                            Square Footage:{' '}
                                        </span>
                                        <span className=''>{formatNumberWithCommas(item.SquareFootage)}</span>
                                    </div>
                                ) : null}
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                <div className='ms-store-select__location-line-item-contact-info'>
                                    <span className='ms-store-select__location-line-header' style={{ textTransform: 'none' }}>
                                        Description:{' '}
                                    </span>
                                    <span className=''>{item.StoreDescription}</span>
                                </div>
                                {item.Contacts.map(({ Name, Title }, j) =>
                                    Name.length && Title.length ? (
                                        <div className='ms-store-select__location-line-item-contact-info' key={j}>
                                            <span className='ms-store-select__location-line-header' style={{ textTransform: 'none' }}>
                                                {Title}:{' '}
                                            </span>
                                            <span className=''>{Name}</span>
                                        </div>
                                    ) : null
                                )}
                            </div>
                        </div>
                    </div>
                    {/* Please do not remove this div below. It's for highlighting on hover the location items */}
                    <div aria-hidden className={classNames(focusedLocationId === item.Id && 'cps-location--focused')}></div>
                </div>
            </div>
        );
    });
    return <>{jsx}</>;
}

function Search() {
    const { context, data } = useContext();
    const [{ AzureMapsApiKey: BackupAzureMapsApiKey }] = React.useState(
        data.fullStoreLocationsWithAvailabilities?.result ?? { AzureMapsApiKey: '' }
    );
    const { BingMapsApiKey } = context.request.channel ?? {};
    const [zip, setZip] = React.useState('');
    const [isInvalidZip, setIsInvalidZip] = React.useState(false);
    return (
        <>
            {isInvalidZip ? <div style={{ transform: 'translateY(25px)', color: 'red' }}>Invalid Zipcode</div> : null}
            <form className='ms-store-select__search-form' id='ms-store-select__search-box-container_store-selector'>
                <input
                    className='msc-form-control ms-store-select__search-input'
                    placeholder='Enter Zip'
                    id='ms-store-select__search-box_store-selector'
                    role='combobox'
                    aria-expanded='false'
                    aria-controls='as_containerSearch'
                    onChange={event => {
                        setIsInvalidZip(false);
                        setZip(event.currentTarget.value);
                    }}
                    value={zip}
                />
                <button
                    onClick={async event => {
                        event.preventDefault();
                        const isValidZip = validateZip(zip);

                        if (!isValidZip) {
                            setIsInvalidZip(true);
                            return;
                        }

                        const {
                            latitude,
                            longitude
                        }: {
                            latitude?: number;
                            longitude?: number;
                        } = await fetch(
                            `https://atlas.microsoft.com/search/address/structured/json?api-version=1.0&subscription-key=${
                                BingMapsApiKey?.length ? BingMapsApiKey : BackupAzureMapsApiKey
                            }&postalCode=${zip.trim()}&countryCode=US&limit=1`
                        )
                            .then(res => res.json())
                            .then(data => {
                                const result = data.results[0];
                                const coors = { latitude: result.position.lat, longitude: result.position.lon } as {
                                    latitude: number;
                                    longitude: number;
                                };
                                return coors;
                            })
                            .catch(() => {
                                window._searchZip = undefined;
                                window._closestLocationId = undefined;
                                window._closestLocationDistance = undefined;
                                setIsInvalidZip(true);
                                return { latitude: undefined, longitude: undefined };
                            });

                        // Find the closest location to the search zip code
                        const closestLocationsByDistance = (data.fullStoreLocationsWithAvailabilities.result?.Locations ?? [])
                            .map(location => {
                                const distance = calcDistance(
                                    [latitude ?? 0, longitude ?? 0],
                                    [location.Latitude ?? 0, location.Longitude ?? 0]
                                );
                                return { ...location, distance };
                            })
                            .sort((a, b) => a.distance - b.distance);
                        const closestLocation = closestLocationsByDistance[0];

                        if (!closestLocation || longitude === undefined || latitude === undefined) {
                            setZip('');
                            return;
                        }

                        window._searchZip = longitude === undefined || latitude === undefined ? undefined : zip.trim();
                        window._closestLocationId = longitude === undefined || latitude === undefined ? undefined : closestLocation.Id;
                        window._closestLocationDistance =
                            longitude === undefined || latitude === undefined
                                ? undefined
                                : (closestLocation.distance * 0.621371).toFixed(1);

                        // Focus the closest location
                        context.actionContext.update<IGetFullStoreLocationsWithAvailabilitiesData>(
                            new GetFullStoreLocationsWithAvailabilitiesInput(),
                            {
                                ...(data.fullStoreLocationsWithAvailabilities.result as IGetFullStoreLocationsWithAvailabilitiesData),
                                SelectedId: closestLocation.Id
                            }
                        );

                        // Set the pushpin info box of the closest location to search point
                        if (window.userMarker) {
                            window.map.markers.remove(window.userMarker);
                        }

                        if (!longitude || !latitude || !globalThis.atlas) {
                            return;
                        }

                        const userMarker = new globalThis.atlas.HtmlMarker({
                            htmlContent: `
                                ${getUserMarkerStyle()}
                                <div class="marker-row">
                                    <div class="map-marker"></div> 
                                </div>`,
                            position: [longitude, latitude]
                        });
                        // <div class='marker-label'>
                        //     <a
                        //         class='zip-close'
                        //         onclick="(() => {
                        //                     const userMarker = new globalThis.atlas.HtmlMarker({
                        //                         htmlContent: \`
                        //                             ${getUserMarkerStyle()}
                        //                             <div class='marker-row'>
                        //                                 <div class='map-marker'></div>
                        //                             </div>\`,
                        //                         position: [${longitude}, ${latitude}]
                        //                     });
                        //                     window.map.markers.remove(window.userMarker)
                        //                     window.userMarker = userMarker;
                        //                     window.map.markers.add(userMarker);
                        //                 })()"
                        //     >
                        //         <img
                        //             class='infobox-close-img'
                        //             src='data:image/svg+xml;base64,PHN2ZyBoZWlnaHQ9IjE0cHgiIHdpZHRoPSIxNHB4IiB4bWxuczp4bGluaz0iaHR0cDovL3d3dy53My5vcmcvMTk5OS94bGluayIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIiB2ZXJzaW9uPSIxLjEiPjxwYXRoIGQ9Ik03LDBDMy4xMzQsMCwwLDMuMTM0LDAsN2MwLDMuODY3LDMuMTM0LDcsNyw3YzMuODY3LDAsNy0zLjEzMyw3LTdDMTQsMy4xMzQsMTAuODY3LDAsNywweiBNMTAuNSw5LjVsLTEsMUw3LDhsLTIuNSwyLjVsLTEtMUw2LDdMMy41LDQuNWwxLTFMNyw2bDIuNS0yLjVsMSwxTDgsN0wxMC41LDkuNXoiLz48L3N2Zz4='
                        //         />
                        //     </a>
                        //     <p>Closest to zip ${zip.trim()}</p>
                        //     <p style='font-weight:bold;'>
                        //         ${closestLocation.StoreName}, ${closestLocation.State} - ${(closestLocation.distance * 0.621371).toFixed(1)}{' '}
                        //         miles
                        //     </p>
                        // </div>;

                        userMarker.getElement().style.zIndex = '6';

                        window.userMarker = userMarker;
                        window.map.markers.add(userMarker);
                        window.infoBox?.remove();
                        window.infoBox = new globalThis.atlas.Popup({
                            content: `
                         <div class="cps-map__info-box">
                             <div>${closestLocation.StoreName} - ${closestLocation.StorePurpose}</div>
                             <div>
                                 <div>${closestLocation.StreetAddress}</div>
                                 <div>${`${closestLocation.City}, ${closestLocation.State} ${closestLocation.Zip}`}</div>
                             </div>
                             <button onclick="(() => (document.querySelector('.cps-location--focused').scrollIntoView({ behavior: 'smooth',block: 'center',inline: 'center' })))()">View Details</button>
                         </div>`,
                            position: [closestLocation.Longitude, closestLocation.Latitude],
                            showPointer: false,
                            pixelOffset: [0, -60]
                        });

                        window.infoBox.open(window.map);

                        // Center map at the location inbetween 2 points
                        // const { center } = zoomAndCenterForTwoEdgeFit(window.map, p1, p2);

                        const centerLatitude = ((closestLocation.Latitude ?? 0) + (latitude ?? 0)) / 2;
                        const centerLongtude = ((closestLocation.Longitude ?? 0) + (longitude ?? 0)) / 2;

                        window.map.setCamera({ center: [centerLongtude, centerLatitude], zoom: 5 });
                        window._noInfoBox = true;

                        window._closestMarkerPosition = [closestLocation.Longitude, closestLocation.Latitude];
                    }}
                    className='ms-store-select__search-button'
                    color='primary'
                ></button>
            </form>
        </>
    );
}

function validateZip(zip: string) {
    // eslint-disable-next-line security/detect-unsafe-regex
    return /^\d{5}(-\d{4})?$/.test(zip);
}

function getFullStateName(key: string) {
    return {
        AL: 'Alabama',
        AK: 'Alaska',
        AZ: 'Arizona',
        AR: 'Arkansas',
        CA: 'California',
        CO: 'Colorado',
        CT: 'Connecticut',
        DE: 'Delaware',
        DC: 'District of Columbia',
        FL: 'Florida',
        GA: 'Georgia',
        HI: 'Hawaii',
        ID: 'Idaho',
        IL: 'Illinois',
        IN: 'Indiana',
        IA: 'Iowa',
        KS: 'Kansas',
        KY: 'Kentucky',
        LA: 'Louisiana',
        ME: 'Maine',
        MD: 'Maryland',
        MA: 'Massachusetts',
        MI: 'Michigan',
        MN: 'Minnesota',
        MS: 'Mississippi',
        MO: 'Missouri',
        MT: 'Montana',
        NE: 'Nebraska',
        NV: 'Nevada',
        NH: 'New Hampshire',
        NJ: 'New Jersey',
        NM: 'New Mexico',
        NY: 'New York',
        NC: 'North Carolina',
        ND: 'North Dakota',
        OH: 'Ohio',
        OK: 'Oklahoma',
        OR: 'Oregon',
        PA: 'Pennsylvania',
        RI: 'Rhode Island',
        SC: 'South Carolina',
        SD: 'South Dakota',
        TN: 'Tennessee',
        TX: 'Texas',
        UT: 'Utah',
        VT: 'Vermont',
        VA: 'Virginia',
        WA: 'Washington',
        WV: 'West Virginia',
        WI: 'Wisconsin',
        WY: 'Wyoming'
    }[`${key}`];
}

function secondsToTime(input: number, leadingZero = false) {
    const converted = input / 3600;
    const integer = Math.floor(converted);
    const decimal = converted - integer;
    const minute = Math.round(decimal * 60).toString();
    const isPM = converted > 12;

    // Don't ask me what this logic is
    if (leadingZero) {
        return `${isPM ? ((integer - 12).toString().length === 2 ? integer - 12 : `0${integer - 12}`) : `0${integer}`}:${
            minute.length === 2 ? minute : `0${minute}`
        } ${isPM ? 'pm' : 'am'}`;
    }

    return `${isPM ? ((integer - 12).toString().length === 2 ? integer - 12 : `${integer - 12}`) : `${integer}`}:${
        minute.length === 2 ? minute : `0${minute}`
    } ${isPM ? 'pm' : 'am'}`;
}

function useContext() {
    return React.useContext(Context);
}

function findLocationById(id: undefined | string, locations: IGetFullStoreLocationsWithAvailabilitiesData['Locations']) {
    return id ? locations.find(({ Id }) => Id === id) : undefined;
}

function calcDistance(coorA: [number, number], coorB: [number, number]) {
    const [lat1, lon1] = coorA;
    const [lat2, lon2] = coorB;
    const r = 6371;
    const p = Math.PI / 180;
    const a = 0.5 - Math.cos((lat2 - lat1) * p) / 2 + (Math.cos(lat1 * p) * Math.cos(lat2 * p) * (1 - Math.cos((lon2 - lon1) * p))) / 2;
    return 2 * r * Math.asin(Math.sqrt(a));
}

function formatNumberWithCommas(number: number) {
    // eslint-disable-next-line security/detect-unsafe-regex
    return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

function getUserMarkerStyle() {
    return `
        <style>
            .map-marker {
                width: 16px;
                height: 16px;
                background-color: #0078d4;
                border: 2px solid #ffffff;
                border-radius: 50%;
                box-shadow: 0 2px 8px rgba(0, 120, 212, 0.4);
                cursor: pointer;
                transition: all 0.2s ease;
                position: relative;
                flex-shrink: 0;
                cursor: default;
            }
            
            .map-marker:hover {
                transform: scale(1.2);
                box-shadow: 0 4px 12px rgba(0, 120, 212, 0.6);
            }
            
            .map-marker::after {
                content: '';
                position: absolute;
                top: 50%;
                left: 50%;
                width: 6px;
                height: 6px;
                background-color: #ffffff;
                border-radius: 50%;
                transform: translate(-50%, -50%);
            }
            
            .marker-label {
                cursor: default;
                position: absolute;
                width: max-content;
                transform: translateX(-50%);
                font-size: 14px;
                color: #323130;
                z-index: 999999;
                background: white;
                padding: 15px;
                border-radius: 5px;
                margin-top: 5px;
                text-align: center;
            }
            
            .zip-close {
                cursor: pointer;
                position: absolute;
                right: 5px;
                top: 5px;
            }
        </style>`;
}
